import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { MessagesSquare, Send, ShieldCheck, ShieldHalf, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import UserNameLink from "./UserNameLink";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function RolePill({ role }) {
  if (role === "admin") return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-red-500/40 text-red-400 font-mono-num text-[9px] uppercase tracking-widest">
      <ShieldCheck className="w-2.5 h-2.5" /> admin
    </span>
  );
  if (role === "moderator") return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#0D9488]/40 text-[#0D9488] font-mono-num text-[9px] uppercase tracking-widest">
      <ShieldHalf className="w-2.5 h-2.5" /> mod
    </span>
  );
  return null;
}

/**
 * Staff-only global chat channel. Visible + writable to admin + moderator.
 * Polls every 8s so the team sees fresh replies without a heavy socket setup.
 */
export default function StaffChatCard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/staff-chat", { params: { limit: 100 } });
      setItems(data.items || []);
    } catch (e) {
      // Silent — non-staff hits 403 but this card only renders for staff.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [items.length]);

  const send = async () => {
    if (!body.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/staff-chat", { body: body.trim() });
      setItems((cur) => [...cur, data]);
      setBody("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    const snapshot = items;
    setItems((cur) => cur.filter((m) => m.id !== id));
    try {
      await api.delete(`/admin/staff-chat/${id}`);
    } catch (e) {
      setItems(snapshot);
      toast.error(formatApiError(e));
    }
  };

  return (
    <Card className="bg-white border border-slate-200 rounded-none p-5 flex flex-col" data-testid="staff-chat-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500">// Staff-only channel</div>
          <div className="font-display font-black uppercase tracking-tight text-lg text-slate-900 flex items-center gap-2">
            <MessagesSquare className="w-4 h-4 text-[#0D9488]" /> Staff Chat
          </div>
        </div>
        <div className="text-[10px] font-mono-num text-slate-500 uppercase tracking-widest">
          {items.length} msg{items.length === 1 ? "" : "s"}
        </div>
      </div>

      <div
        ref={scrollRef}
        className="border border-slate-200 bg-slate-50/40 h-64 overflow-y-auto p-3 space-y-3"
        data-testid="staff-chat-messages"
      >
        {loading ? (
          <div className="text-slate-500 font-mono-num text-xs animate-pulse">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-slate-500 text-sm text-center py-8">
            <div className="font-mono-num text-[10px] uppercase tracking-widest">// No messages yet</div>
            <div className="text-xs mt-1">Say hi to the team.</div>
          </div>
        ) : items.map((m) => {
          const mine = m.author_id === user?.id;
          const canDelete = mine || isAdmin;
          return (
            <div key={m.id} className="flex items-start gap-2 group" data-testid={`staff-chat-msg-${m.id}`}>
              <div className="w-7 h-7 shrink-0 border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                {m.author_avatar_url ? (
                  <img src={m.author_avatar_url} alt={m.author_name} className="w-full h-full object-cover" />
                ) : (
                  <span className="font-display font-black text-xs text-slate-500">{(m.author_name || "?")[0].toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <UserNameLink
                    userId={m.author_id}
                    name={m.author_name}
                    className="text-slate-900 text-sm font-medium"
                    showUnderline={false}
                    testid={`staff-chat-author-${m.id}`}
                  />
                  <RolePill role={m.author_role} />
                  <span className="font-mono-num text-[10px] text-slate-500 uppercase tracking-widest">{timeAgo(m.created_at)}</span>
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => del(m.id)}
                      className="ml-auto opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-all"
                      data-testid={`staff-chat-delete-${m.id}`}
                      title="Delete message"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap break-words leading-relaxed">{m.body}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex items-end gap-2">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, 2000))}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
          rows={2}
          placeholder="Message the team… (⌘/Ctrl-Enter to send)"
          className="bg-slate-50 border-slate-200 rounded-none resize-none text-sm flex-1"
          data-testid="staff-chat-composer"
        />
        <Button
          type="button"
          onClick={send}
          disabled={!body.trim() || sending}
          className="h-16 rounded-none bg-[#0D9488] hover:bg-[#0F766E] text-black uppercase tracking-widest text-xs font-semibold px-4"
          data-testid="staff-chat-send"
        >
          <Send className="w-3.5 h-3.5 mr-1.5" /> Send
        </Button>
      </div>
      <div className="mt-1 text-right text-[10px] font-mono-num text-slate-400">{body.length}/2000</div>
    </Card>
  );
}
